(() => {
  if (window.__hieugklV3Ready) return;
  window.__hieugklV3Ready = true;
  const instances = new WeakMap();
  const heroInstances = new WeakMap();

  function initializeHero(hero) {
    if (heroInstances.has(hero)) return;
    const viewport = hero.querySelector('[data-hkv6-viewport]');
    const slides = Array.from(hero.querySelectorAll('[data-hkv6-slide]'));
    const dots = Array.from(hero.querySelectorAll('[data-hkv6-dot]'));
    const previous = hero.querySelector('[data-hkv6-prev]');
    const next = hero.querySelector('[data-hkv6-next]');
    const toggle = hero.querySelector('[data-hkv6-toggle]');
    const controls = hero.querySelector('[data-hkv6-controls]');
    const status = hero.querySelector('[data-hkv6-status]');
    if (!viewport || slides.length < 2 || !previous || !next || !toggle || !controls) return;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let current = 0;
    let userStopped = motion.matches || hero.dataset.hkv6Autoplay === 'false';
    let hovered = false;
    let focused = hero.contains(document.activeElement);
    let inView = false;
    let timer = 0;
    let focusTimer = 0;
    let gesture = null;
    let suppressClickUntil = 0;

    function updateControls() {
      toggle.setAttribute('aria-label', userStopped ? 'Play slideshow' : 'Pause slideshow');
      toggle.querySelector('[data-hkv6-pause-icon]').toggleAttribute('hidden', userStopped);
      toggle.querySelector('[data-hkv6-play-icon]').toggleAttribute('hidden', !userStopped);
      hero.dataset.hkv6Index = String(current);
      hero.dataset.hkv6Rotation = timer ? 'playing' : 'paused';
    }

    function schedule() {
      window.clearTimeout(timer);
      timer = 0;
      if (!userStopped && !hovered && !focused && inView && !document.hidden) {
        timer = window.setTimeout(() => show(current + 1, false), 7000);
      }
      updateControls();
    }

    function show(index, manual = true) {
      const target = (index + slides.length) % slides.length;
      if (manual) userStopped = true;
      if (target !== current && slides[current].contains(document.activeElement)) {
        viewport.focus({ preventScroll: true });
      }
      current = target;
      slides.forEach((slide, number) => {
        const active = number === current;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        if (active) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      dots.forEach((dot, number) => {
        if (number === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (manual && status) status.textContent = `${slides[current].dataset.hkv6Title}. Slide ${current + 1} of ${slides.length}.`;
      schedule();
    }

    previous.addEventListener('click', () => show(current - 1), options);
    next.addEventListener('click', () => show(current + 1), options);
    dots.forEach((dot, number) => dot.addEventListener('click', () => show(number), options));
    toggle.addEventListener('click', () => { userStopped = !userStopped; schedule(); }, options);
    viewport.addEventListener('keydown', event => {
      if (event.target !== viewport || event.altKey || event.ctrlKey || event.metaKey) return;
      const targets = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1 };
      if (Object.prototype.hasOwnProperty.call(targets, event.key)) {
        event.preventDefault();
        show(targets[event.key]);
      }
    }, options);
    hero.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    }, options);
    hero.addEventListener('pointerleave', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = false;
      schedule();
    }, options);
    hero.addEventListener('focusin', () => { focused = true; schedule(); }, options);
    hero.addEventListener('focusout', () => {
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => { focused = hero.contains(document.activeElement); schedule(); }, 0);
    }, options);
    document.addEventListener('visibilitychange', schedule, options);
    motion.addEventListener('change', event => {
      if (event.matches) userStopped = true;
      schedule();
    }, options);

    viewport.addEventListener('pointerdown', event => {
      if (!event.isPrimary || !['touch', 'pen'].includes(event.pointerType)) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, options);
    viewport.addEventListener('pointerup', event => {
      if (!gesture || event.pointerId !== gesture.id) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      gesture = null;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        suppressClickUntil = Date.now() + 450;
        show(current + (dx < 0 ? 1 : -1));
      }
    }, options);
    viewport.addEventListener('pointercancel', () => { gesture = null; }, options);
    viewport.addEventListener('click', event => {
      if (Date.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopPropagation();
    }, { ...options, capture: true });

    let observer;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .2;
        schedule();
      }, { threshold: [0, .2] });
      observer.observe(hero);
    } else inView = true;
    slides.slice(1).forEach(slide => slide.querySelectorAll('img').forEach(img => { img.loading = 'eager'; img.fetchPriority = 'low'; }));
    controls.hidden = false;
    show(0, false);
    heroInstances.set(hero, () => {
      controller.abort();
      observer?.disconnect();
      window.clearTimeout(timer);
      window.clearTimeout(focusTimer);
      heroInstances.delete(hero);
    });
  }

  function initialize(gallery) {
    if (instances.has(gallery)) return;
    const rail = gallery.querySelector('[data-hkv3-rail]');
    const slides = Array.from(gallery.querySelectorAll('[data-hkv3-slide]'));
    const controls = gallery.querySelector('[data-hkv3-controls]');
    const previous = gallery.querySelector('[data-hkv3-prev]');
    const next = gallery.querySelector('[data-hkv3-next]');
    const status = gallery.querySelector('[data-hkv3-status]');
    const itemLabel = gallery.dataset.hkv3ItemLabel || 'Scene';
    const itemsLabel = gallery.dataset.hkv3ItemsLabel || 'Scenes';
    if (!rail || !slides.length || !controls || !previous || !next) return;

    const controller = new AbortController();
    const options = { signal: controller.signal };
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;

    function positions() {
      const railLeft = rail.getBoundingClientRect().left;
      return slides.map(slide => slide.getBoundingClientRect().left - railLeft + rail.scrollLeft);
    }

    function update() {
      frame = 0;
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const current = Math.max(0, rail.scrollLeft);
      previous.disabled = current <= 2 || maximum <= 2;
      next.disabled = current >= maximum - 2 || maximum <= 2;
      const visible = slides.reduce((indices, slide, index) => {
        const rect = slide.getBoundingClientRect();
        const viewport = rail.getBoundingClientRect();
        if (rect.right > viewport.left + 8 && rect.left < viewport.right - 8) indices.push(index + 1);
        return indices;
      }, []);
      if (status && visible.length) {
        const text = visible.length === 1 ? `${itemLabel} ${visible[0]} of ${slides.length}.` : `${itemsLabel} ${visible[0]} to ${visible[visible.length - 1]} of ${slides.length}.`;
        if (status.textContent !== text) status.textContent = text;
      }
    }

    function scheduleUpdate() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    function goTo(position) {
      const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
      rail.scrollTo({ left: Math.max(0, Math.min(position, maximum)), behavior: motion.matches ? 'auto' : 'smooth' });
      scheduleUpdate();
    }

    function move(direction) {
      const stops = positions();
      const current = rail.scrollLeft;
      if (direction > 0) goTo(stops.find(stop => stop > current + 3) ?? rail.scrollWidth);
      else goTo(stops.slice().reverse().find(stop => stop < current - 3) ?? 0);
    }

    previous.addEventListener('click', () => move(-1), options);
    next.addEventListener('click', () => move(1), options);
    rail.addEventListener('scroll', scheduleUpdate, { ...options, passive: true });
    rail.addEventListener('keydown', event => {
      if (event.target !== rail || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        goTo(event.key === 'Home' ? 0 : rail.scrollWidth);
      }
    }, options);

    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(scheduleUpdate);
      observer.observe(rail);
    } else window.addEventListener('resize', scheduleUpdate, options);
    controls.hidden = false;
    update();
    instances.set(gallery, () => {
      controller.abort();
      observer?.disconnect();
      if (frame) cancelAnimationFrame(frame);
      instances.delete(gallery);
    });
  }

  function galleriesWithin(root) {
    const galleries = Array.from(root.querySelectorAll('[data-hkv3-gallery]'));
    if (root.matches?.('[data-hkv3-gallery]')) galleries.unshift(root);
    return galleries;
  }
  function heroesWithin(root) {
    const heroes = Array.from(root.querySelectorAll('[data-hkv6-hero]'));
    if (root.matches?.('[data-hkv6-hero]')) heroes.unshift(root);
    return heroes;
  }
  function mount(root = document) {
    galleriesWithin(root).forEach(initialize);
    heroesWithin(root).forEach(initializeHero);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    galleriesWithin(event.target).forEach(gallery => instances.get(gallery)?.());
    heroesWithin(event.target).forEach(hero => heroInstances.get(hero)?.());
  });
})();
